import React from 'react';
import {
    Box,
    useScrollTrigger,
    Zoom
} from '@mui/material';
import { createTheme } from '@mui/material/styles';


// Vibrant professional color theme
export const getTheme = (mode) => createTheme({
    palette: {
        mode,
        primary: {
            main: mode === 'dark' ? '#6a1bff' : '#5e00ff',
            light: '#9a4dff',
            dark: '#3f00b2',
        },
        secondary: {
            main: '#00e5ff',
            light: '#66ffff',
            dark: '#00b2cc',
        },
        accent: {
            main: '#ff3d00',
            light: '#ff7539',
            dark: '#c30000',
        },
        background: {
            default: mode === 'dark' ? '#0a0a1a' : '#f5f7ff',
            paper: mode === 'dark' ? '#1a1a2e' : '#ffffff',
        },
        text: {
            primary: mode === 'dark' ? '#f0f4ff' : '#0a0a1a',
            secondary: mode === 'dark' ? '#c7d2ff' : '#4a4a6a',
        },
        gradients: {
            primary: mode === 'dark'
                ? 'linear-gradient(135deg, #6a1bff 0%, #9a4dff 100%)'
                : 'linear-gradient(135deg, #5e00ff 0%, #9a4dff 100%)',
            secondary: 'linear-gradient(135deg, #00e5ff 0%, #00b2cc 100%)',
            accent: 'linear-gradient(135deg, #ff3d00 0%, #ff7539 100%)',
        },
    },
    typography: {
        fontFamily: [
            '"Inter"',
            '"Poppins"',
            '"Roboto"',
            '"Helvetica"',
            '"Arial"',
            'sans-serif'
        ].join(','),
        h1: {
            fontWeight: 800,
            fontSize: '3.5rem',
            lineHeight: 1.2,
        },
        h2: {
            fontWeight: 700,
            fontSize: '2.75rem',
            lineHeight: 1.3,
        },
        h3: {
            fontWeight: 700,
            fontSize: '2.25rem',
            lineHeight: 1.3,
        },
        h4: {
            fontWeight: 600,
            fontSize: '1.75rem',
            lineHeight: 1.4,
        },
        h5: {
            fontWeight: 600,
            fontSize: '1.5rem',
            lineHeight: 1.4,
        },
        h6: {
            fontWeight: 500,
            fontSize: '1.25rem',
            lineHeight: 1.5,
        },
        subtitle1: {
            fontWeight: 500,
            fontSize: '1rem',
            lineHeight: 1.5,
        },
        subtitle2: {
            fontWeight: 500,
            fontSize: '0.875rem',
            lineHeight: 1.5,
        },
        body1: {
            fontWeight: 400,
            fontSize: '1rem',
            lineHeight: 1.6,
        },
        body2: {
            fontWeight: 400,
            fontSize: '0.875rem',
            lineHeight: 1.6,
        },
        button: {
            fontWeight: 600,
            textTransform: 'none',
            letterSpacing: '0.025em',
        },
    },
    shape: {
        borderRadius: 12,
    },
    shadows: mode === 'dark' ? [
        'none',
        '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
        '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
        '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
        '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        '0 25px 50px -12px rgba(106, 27, 255, 0.25)',
        '0 25px 50px -12px rgba(0, 229, 255, 0.25)',
        '0 25px 50px -12px rgba(255, 61, 0, 0.25)',
    ] : [
        'none',
        '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
        '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
        '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
        '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        '0 25px 50px -12px rgba(94, 0, 255, 0.15)',
        '0 25px 50px -12px rgba(0, 229, 255, 0.15)',
        '0 25px 50px -12px rgba(255, 61, 0, 0.15)',
    ],
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    padding: '10px 24px',
                    boxShadow: 'none',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: mode === 'dark'
                            ? '0 6px 20px 0 rgba(106, 27, 255, 0.3)'
                            : '0 6px 20px 0 rgba(94, 0, 255, 0.15)',
                    },
                },
                contained: {
                    background: mode === 'dark'
                        ? 'linear-gradient(135deg, #6a1bff 0%, #9a4dff 100%)'
                        : 'linear-gradient(135deg, #5e00ff 0%, #9a4dff 100%)',
                    color: '#fff',
                    '&:hover': {
                        background: mode === 'dark'
                            ? 'linear-gradient(135deg, #5a00e0 0%, #8a45e0 100%)'
                            : 'linear-gradient(135deg, #5200e0 0%, #8a45e0 100%)',
                    },
                },
                outlined: {
                    borderWidth: '2px',
                    '&:hover': {
                        borderWidth: '2px',
                    },
                },
                text: {
                    '&:hover': {
                        backgroundColor: mode === 'dark'
                            ? 'rgba(106, 27, 255, 0.08)'
                            : 'rgba(94, 0, 255, 0.04)',
                    },
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    boxShadow: mode === 'dark'
                        ? '0 4px 20px 0 rgba(0, 0, 0, 0.2)'
                        : '0 4px 20px 0 rgba(0, 0, 0, 0.05)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                        transform: 'translateY(-5px)',
                        boxShadow: mode === 'dark'
                            ? '0 8px 25px 0 rgba(0, 0, 0, 0.3)'
                            : '0 8px 25px 0 rgba(0, 0, 0, 0.1)',
                    },
                },
            },
        },
        MuiAppBar: {
            styleOverrides: {
                root: {
                    boxShadow: mode === 'dark'
                        ? '0 2px 10px 0 rgba(0, 0, 0, 0.2)'
                        : '0 2px 10px 0 rgba(0, 0, 0, 0.05)',
                    background: mode === 'dark'
                        ? 'rgba(10, 10, 26, 0.9)'
                        : 'rgba(245, 247, 255, 0.9)',
                    backdropFilter: 'blur(12px)',
                    borderBottom: mode === 'dark'
                        ? '1px solid rgba(106, 27, 255, 0.1)'
                        : '1px solid rgba(94, 0, 255, 0.05)',
                },
            },
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                            borderColor: mode === 'dark' ? '#6a1bff' : '#5e00ff',
                        },
                        '&.Mui-focused fieldset': {
                            borderColor: mode === 'dark' ? '#6a1bff' : '#5e00ff',
                            boxShadow: mode === 'dark'
                                ? '0 0 0 2px rgba(106, 27, 255, 0.2)'
                                : '0 0 0 2px rgba(94, 0, 255, 0.1)',
                        },
                    },
                },
            },
        },
        MuiTabs: {
            styleOverrides: {
                indicator: {
                    height: 4,
                    borderRadius: 2,
                    background: mode === 'dark'
                        ? 'linear-gradient(90deg, #6a1bff, #00e5ff)'
                        : 'linear-gradient(90deg, #5e00ff, #00e5ff)',
                },
            },
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '1rem',
                    '&.Mui-selected': {
                        color: mode === 'dark' ? '#6a1bff' : '#5e00ff',
                    },
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    fontWeight: 500,
                },
                colorPrimary: {
                    background: mode === 'dark'
                        ? 'rgba(106, 27, 255, 0.2)'
                        : 'rgba(94, 0, 255, 0.1)',
                    color: mode === 'dark' ? '#9a4dff' : '#5e00ff',
                },
                colorSecondary: {
                    background: mode === 'dark'
                        ? 'rgba(0, 229, 255, 0.2)'
                        : 'rgba(0, 229, 255, 0.1)',
                    color: mode === 'dark' ? '#66ffff' : '#00b2cc',
                },
            },
        },
    },
});







// Animation variants
export const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.6,
            ease: "easeOut"
        }
    }
};

export const fadeIn = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            duration: 0.8,
            ease: "easeOut"
        }
    }
};

export const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.2
        }
    }
};

export const pulseAnimation = {
    initial: { scale: 1 },
    animate: {
        scale: [1, 1.05, 1],
        transition: {
            duration: 3,
            repeat: Infinity,
            repeatType: "reverse",
            ease: "easeInOut"
        }
    }
};

export const floatAnimation = {
    initial: { y: 0 },
    animate: {
        y: [0, -15, 0],
        transition: {
            duration: 6,
            repeat: Infinity,
            repeatType: "reverse",
            ease: "easeInOut"
        }
    }
};

export const rotateAnimation = {
    initial: { rotate: 0 },
    animate: {
        rotate: 360,
        transition: {
            duration: 20,
            repeat: Infinity,
            repeatType: "loop",
            ease: "linear"
        }
    }
};


export const fadeInLeft = {
    hidden: { opacity: 0, x: -20 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.6 } }
};

export const fadeInRight = {
    hidden: { opacity: 0, x: 20 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.6 } }
};



// Scroll to top component
const ScrollTop = (props) => {
    const { children } = props;
    const trigger = useScrollTrigger({
        disableHysteresis: true,
        threshold: 100,
    });

    const handleClick = () => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth',
        });
    };

    return (
        <Zoom in={trigger}>
            <Box
                onClick={handleClick}
                role="presentation"
                sx={{
                    position: 'fixed',
                    bottom: 32,
                    right: 32,
                    zIndex: 999,
                }}
            >
                {children}
            </Box>
        </Zoom>
    );
}
export default ScrollTop;